<script setup lang="ts">
import type { PythonErrorInfo } from '~/utils/pyxelRuntime'
import { parsePythonError, pyxelCdnBase, resolvePyxelVersion } from '~/utils/pyxelRuntime'

// #746: Pyxel ランタイム版は下の PYXEL_CDN（props/クエリ解決後に確定）へ集約。既定 2.9.5、
// `?pyxel=<x.y.z>` または `pyxelVersion` prop で上書き可。#685 の固定方針の経緯と、pyxel 2.9.6
// で同梱 Pyodide が 314 系（em5/Py3.14）に上がり iOS Safari で全滅した回帰の背景は
// doc/architecture/csp.md / pyxel_runtime.md を参照。

// Pyodide は Pyxel WASM が内部でロードする（バージョンは Pyxel に従属）。CSP は prefix 一致で
// 許可するため、ここはバージョン無指定のままでよい（Pyxel を固定すれば Pyodide も実質固定される）。
const PYODIDE_CDN = 'https://cdn.jsdelivr.net/pyodide/'

interface Props {
  root?: string
  entrypoint?: string
  code?: string
  mode?: string
  /** When true, expose the iframe ref for external canvas capture */
  capture?: boolean
  /**
   * #728 security boundary. Default (false/undefined) renders the play iframe on
   * an OPAQUE origin — sandbox WITHOUT `allow-same-origin` — so a work that escapes
   * to `js` cannot reach the app origin's localStorage (auth token), `window.parent`,
   * or credentialed same-origin fetch. Set true ONLY for trusted **own-content**
   * authoring flows that must introspect the iframe from the parent.
   * NOTE (#749): `capture` no longer implies same-origin. When a sandbox origin is
   * configured, `capture` runs cross-origin like the play path and is driven via the
   * `__pyxelCmd` protocol (see useNativeScreencast). Only the blob fallback (no
   * sandbox origin) still grants `capture` same-origin so the parent can drive it.
   */
  trusted?: boolean
  /**
   * When true, fill the parent container instead of the centering43 4:3 sizing.
   * Note: this is NOT Browser Fullscreen API (Alt+Enter). Pyxel's Alt+Enter
   * fullscreen is handled by Pyxel itself via SDL2 → Browser Fullscreen API.
   */
  fillParent?: boolean
  /**
   * When true (and on a touch-primary mobile device), output `gamepad="enabled"`
   * to <pyxel-run>. Should be set only for works whose code handles gamepad keys.
   */
  enableGamepad?: boolean
  /**
   * Developer test mode for the playground:
   * - force `(pointer: coarse)` to true so Pyxel renders the virtual gamepad on PC
   * - relay mouse events as touch events so the gamepad responds to mouse clicks.
   * Used in Playground.vue's "ゲームパッドで操作" toggle.
   */
  gamepadEmulator?: boolean
  /**
   * Comma-separated Pyodide built-in package names to pre-load (e.g. `"numpy,pillow"`).
   * Forwarded to `<pyxel-run packages="...">` / `<pyxel-play packages="...">`. The
   * list is computed server-side from the code's AST and constrained to the
   * Pyodide-builtin whitelist (#663). Empty / undefined → no `packages` attribute.
   */
  packages?: string
  /**
   * #450: when set, enable cloud save. Injects the Emscripten FS-hook script
   * (detects writes under the tracked prefixes, pre-injects prefetched files) and wires the
   * parent-side bridge (`useCloudSave`) that persists to the server / localStorage.
   * The bridge resolves the save scope server-side from this session id.
   */
  cloudSaveSessionId?: string
  /**
   * #450: cloud-save scope (app identity) from `CreateSessionResponse.saveScope`.
   * Only used to namespace localStorage for unauthenticated players.
   */
  cloudSaveScope?: string | null
  /**
   * #737: Pyxel 既定の "CLICK/TOUCH TO START" ゲートをスキップする。親フレームで
   * 既にジェスチャ済みの **無音インラインデモ**（ランディングのヒーロー等）専用。
   * 仕組み: pyxel.js の _waitForInput() は body クリック待ちだが、
   * window.pyxelContext.resolveInput() を呼べばタップ無しで即開始できる。
   * 音声つき作品では WebAudio の autoplay policy により実ジェスチャが要るため使わない。
   */
  autoStart?: boolean
  /**
   * #746: Pyxel ランタイム版の明示上書き（`x.y.z`）。未指定なら `?pyxel=` クエリ →
   * 既定 2.9.5 の順で解決。無効な値は既定へフォールバックする。
   */
  pyxelVersion?: string
}

const props = defineProps<Props>()

// #745: 実行して Python が止まったら親に知らせる（センパイの自発ヒントが使う）。
const emit = defineEmits<{
  runtimeError: [info: PythonErrorInfo]
}>()
const runtimeConfig = useRuntimeConfig()
const { isMobile } = useIsMobile()

// #746: Pyxel ランタイム版を解決。既定 2.9.5、`?pyxel=<x.y.z>`（無効値は既定へフォールバック）
// または `pyxelVersion` prop で上書き可。CSP の script-src prefix（buildCspMeta）と iframe の
// <script src>（htmlTemplate）の双方がこの PYXEL_CDN から生成され、常に同一バージョンで揃う。
// #748: cross-origin フレーム経路では版文字列を pyxel-frame.html?pyxel= に渡す（フレーム側が
// 同じ検証で CDN を組み立てる）。
const route = useRoute()
const pyxelVersionStr = resolvePyxelVersion(props.pyxelVersion ?? route.query.pyxel)
const PYXEL_CDN = pyxelCdnBase(pyxelVersionStr)

const iframeRef = ref<HTMLIFrameElement | null>(null)

defineExpose({ iframeEl: iframeRef })

// #748/#749: cross-origin サンドボックスフレームを使うか（詳細は下部「cross-origin サンドボックス
// フレーム経路」セクション）。runtimeConfig.public.pyxelSandboxOrigin が設定されていて、かつ
// **`trusted` でない**ときに true。
// - #748 Phase 1: 未信頼プレイのみ（`!capture && !trusted`）を cross-origin 化した。
// - #749 Phase 2: `capture`（クリスタル化プレビューの GIF キャプチャ）も cross-origin 化する。
//   親（useNativeScreencast）が contentWindow を直駆動していた部分を、frame 内コントローラ +
//   __pyxelCmd protocol へ付け替えたので `!capture` ゲートを外した。`capture` の config は既に
//   frame へ送っており、frame 側は installSavePyxelFileOverride + installCaptureController を持つ。
// - `!trusted` は温存: 明示的に親からの introspection を要求する own-content 経路の逃げ道
//   （現状 `trusted` を true にする利用箇所は無い）。sandboxOrigin 空 or trusted のときは従来の
//   同一オリジン blob 経路（htmlSrc / sandboxAttr）へフォールバック。
// cloud save の frameOrigin 厳格化にも使うため、cloud save ブロックより前で宣言する。
const sandboxOrigin = computed(() => runtimeConfig.public.pyxelSandboxOrigin || '')
const useCrossOrigin = computed(() =>
  import.meta.client && !!sandboxOrigin.value && !props.trusted,
)

// #450 / #684: FS ブリッジの親側。`cloudSaveSessionId` が来ているときだけ有効で、
// **1 本のフック注入に対して親側は prefix ごとに 2 つのブリッジ**が乗る
// （`.pyxel/` = セーブ / `shared/` = 共有ランキング）。ゲート prop が 1 本なのは、
// どちらもセッション（= アプリ同一性 scope）だけで成立するため。
const cloudSave = useCloudSave()
const sharedData = useSharedData()
function startFsBridgesIfEnabled() {
  if (props.cloudSaveSessionId && iframeRef.value) {
    // #748: cross-origin 経路のみ frameOrigin を渡して送受信 origin を厳格化。
    const frameOrigin = useCrossOrigin.value ? sandboxOrigin.value : undefined
    cloudSave.start(iframeRef.value, {
      sessionId: props.cloudSaveSessionId,
      scope: props.cloudSaveScope ?? null,
      frameOrigin,
    })
    sharedData.start(iframeRef.value, {
      sessionId: props.cloudSaveSessionId,
      frameOrigin,
    })
  }
}
function stopFsBridges() {
  cloudSave.stop()
  sharedData.stop()
}
// ⚠️ **`iframeRef` も監視する**。iframe は `<ClientOnly>` の中にあるので、
// この component の `onMounted` 時点では **まだ存在しない**（ClientOnly が自分の
// onMounted でフラグを立て、次の描画で初めて要素が生える）。
// sessionId だけを見ていると、`PlayDialog`（= 親の onMounted で同じ tick に
// sessionId を入れる経路 ＝ プレイグラウンドの「コードを実行」と、ゆりかごの再生）
// では **iframeRef が null のまま watch が 1 度発火して終わり**、ブリッジが
// 一生張られない（＝ セーブが黙って消える）。`WorkPlay` は fetch 後の後の tick で
// 入れるので偶然動いていた —— 経路によって効く/効かないの差はこれが原因だった。
watch([() => props.cloudSaveSessionId, iframeRef], () => {
  stopFsBridges()
  startFsBridgesIfEnabled()
}, { immediate: true })
onUnmounted(stopFsBridges)

// #728: default-deny origin isolation for the **blob fallback** (no sandbox origin).
// `allow-same-origin` is granted ONLY to trusted own-content flows (`capture` /
// explicit `trusted`) so the parent can introspect `contentWindow`. Untrusted
// published works run on an OPAQUE origin so a `js`-escape cannot touch the app
// origin (localStorage auth token / window.parent / credentialed fetch).
// NOTE (#749): when a sandbox origin IS configured, `capture` takes the cross-origin
// path (iframeSandbox below) and is driven via postMessage — this `sandboxAttr` only
// applies to the blob fallback.
const sandboxAttr = computed(() => {
  const sameOrigin = props.trusted || props.capture
  return ['allow-scripts', sameOrigin ? 'allow-same-origin' : '', 'allow-downloads']
    .filter(Boolean)
    .join(' ')
})

// #728 hardening: Permissions Policy for the play iframe.
// - `fullscreen`: Pyxel の Alt+Enter は SDL2 → Browser Fullscreen API を叩く。これは
//   Permissions Policy `fullscreen`（既定 allowlist = `self`）でゲートされ、opaque origin
//   の iframe は `self` に一致しないため、Tier A（allow-same-origin 除去）以降 `allow=
//   "fullscreen"` が無いと `document.fullscreenEnabled === false` で **Alt+Enter が壊れる**。
//   明示付与で復旧する（opaque + `allow="fullscreen"` で `fullscreenEnabled === true` を実測確認済）。
// - `gamepad`: 物理ゲームパッド（Gamepad API）も同様に opaque では既定で不可 → 明示付与で復旧。
// - camera / microphone / geolocation / usb / payment: Pyxel は使わない。opaque では既定でも
//   拒否されるが、trusted(same-origin) 経路も含め **明示的に無効化**して権限プロンプト詐取を封じる。
// 同一属性を trusted / untrusted 共通で付与する（付与は無害、trusted 側は元々 fullscreen 可）。
const allowAttr
  = 'fullscreen; gamepad; camera \'none\'; microphone \'none\'; geolocation \'none\'; usb \'none\'; payment \'none\''

const bodyStyle = 'width: 90%;'

/**
 * Build a CSP meta tag for the iframe HTML.
 *
 * Goals (#556):
 * - Block external script loading (only Pyxel CDN allowed)
 * - Block external network communication (fetch / XHR / WebSocket)
 * - Block nested iframes
 * - Allow Pyxel WASM to run (needs 'unsafe-eval' for Emscripten)
 *
 * connect-src is dynamic: the root URL origin is whitelisted so pyxel.js
 * can fetch game files (.py, .pyxres). The storage origin (CloudFront in
 * prod, MinIO in local) is also included because the play handler returns
 * a 302 redirect to the actual file location.
 */
function buildCspMeta(rootUrl?: string): string {
  // Origins that pyxel.js needs to fetch from (WASM binary, Pyodide, game files)
  const connectSources = [PYXEL_CDN, PYODIDE_CDN]

  // Storage origin: redirect target of the play handler
  // - prod: CloudFront (serviceDomain)
  // - local: MinIO (storageOrigin env var)
  const storageOrigin = runtimeConfig.public.storageOrigin || runtimeConfig.public.serviceDomain
  if (storageOrigin) {
    connectSources.push(storageOrigin)
  }

  if (rootUrl) {
    try {
      const origin = new URL(rootUrl).origin
      if (!connectSources.includes(origin)) {
        connectSources.push(origin)
      }
    }
    catch { /* inline code mode — no root URL */ }
  }

  // `'unsafe-inline'` は常時許可: Pyodide 0.29.x が boot 時に内部 inline script を実行する
  // ため、これを欠くと CSP 違反でブロックされる。capture / gamepadEmulator モードで注入する
  // ヘルパー inline script もこの設定で許可される。
  // CSP のセキュリティ境界は connect-src（外部通信制限）+ sandbox + frame-src 'none' が主で、
  // 'unsafe-inline' を許可しても実害は限定的。
  const scriptSrc = `script-src ${PYXEL_CDN} ${PYODIDE_CDN} 'unsafe-eval' 'unsafe-inline'`

  const directives = [
    'default-src \'none\'',
    scriptSrc,
    `style-src 'unsafe-inline' ${PYXEL_CDN}`,
    `connect-src ${connectSources.join(' ')}`,
    `img-src ${PYXEL_CDN} data: blob:`,
    'media-src data: blob:',
    'font-src data:',
    'worker-src blob:',
    'frame-src \'none\'',
    'object-src \'none\'',
    'base-uri \'none\'',
  ]
  return `<meta http-equiv="Content-Security-Policy" content="${directives.join('; ')}">`
}

/**
 * Helper script injected BEFORE pyxel.js when capture mode is on (#374).
 *
 * Replaces `window._savePyxelFile` so that Pyxel native screenshot/screencast
 * output (Alt+1/Alt+3 and `pyxel.screenshot()` / `pyxel.screencast()`) delivers
 * a Blob to the parent frame via postMessage instead of triggering a browser
 * download. This lets the crystallize preview auto-capture high-quality GIFs
 * by calling `pyxel.screencast()` on a setInterval from the parent.
 *
 * Mechanism: install an Object.defineProperty setter trap on
 * `window._savePyxelFile` BEFORE pyxel.js runs. When pyxel.js's
 * `_hookFileOperations` assigns the original function, the trap captures it
 * and swaps in our replacement — race-free, since this script runs before
 * pyxel.js loads.
 *
 * Override precedent in C&M: PyxelResourceEditor.vue:101-106 (#439, in
 * production for ~1 year across multiple Pyxel versions). Override precedent
 * upstream: kitao's own code-maker (`web/code-maker/index.html:888`).
 *
 * The parent listens for postMessage `{ type: '__pyxelCapture', kind, filename, blob }`.
 * Unhandled extensions (.pyxres, .pyxpal, etc.) fall through to Pyxel's
 * original download behavior.
 */
const savePyxelFileOverridePatch = `<SCR>
(function() {
  var origSave = null;
  Object.defineProperty(window, '_savePyxelFile', {
    configurable: true,
    set: function(fn) {
      origSave = fn;
      Object.defineProperty(window, '_savePyxelFile', {
        configurable: true, writable: true, value: replacement
      });
    },
    get: function() { return origSave; }
  });

  // Called synchronously from inside wasm: NEVER let anything throw out of here
  // (Pyodide treats an escaped FS.ErrnoError as fatal). #959: with zero recorded
  // frames (e.g. pyxel.init(capture_sec=0)) Pyxel writes no GIF yet still calls
  // us — report it as an empty capture instead of handing it to origSave.
  // Twin of public/pyxel-frame.html's replacement; keep the two in sync.
  function replacement(filename) {
    var basename = filename.split(/[\\\\/]/).pop();
    var ext = basename.toLowerCase().split('.').pop();
    var isCapture = ext === 'gif' || ext === 'png';
    var kind = ext === 'gif' ? 'screencast' : 'screenshot';
    try {
      var ctx = window.pyxelContext;
      var fs = ctx && ctx.pyodide && ctx.pyodide.FS;
      if (!fs) throw new Error('Pyodide FS not available');
      if (!fs.analyzePath(filename).exists) {
        if (isCapture) {
          window.parent.postMessage(
            { type: '__pyxelCapture', kind: kind, filename: basename, empty: true }, '*'
          );
        }
        return;
      }
      if (isCapture) {
        var data = fs.readFile(filename);
        var mime = ext === 'gif' ? 'image/gif' : 'image/png';
        var blob = new Blob([data], { type: mime });
        console.log('[Pyxel hook]', kind + ':', basename, blob.size, 'bytes');
        window.parent.postMessage(
          { type: '__pyxelCapture', kind: kind, filename: basename, blob: blob }, '*'
        );
        return;
      }
    } catch (e) {
      console.error('[Pyxel hook] error:', e);
      return;
    }
    // .pyxres / .pyxpal / dump_palette / dump_image_bank etc. → original download
    try {
      if (typeof origSave === 'function') origSave(filename);
    } catch (e) {
      console.error('[Pyxel hook] original save failed:', e);
    }
  }
})();
</SCR>`.replaceAll('SCR', 'script')

/**
 * Helper script injected BEFORE pyxel.js when gamepadEmulator is on.
 * Lets the developer test their gamepad-handling code on PC:
 *   1. force (pointer: coarse) to true so Pyxel renders the virtual gamepad
 *   2. relay mouse events as touch events so gamepad clicks reach Pyxel
 * Pyxel internals are touch-only; this script bridges the gap.
 */
const gamepadEmulatorPatch = `<SCR>
(function() {
  var orig = window.matchMedia.bind(window);
  window.matchMedia = function(query) {
    if (query === '(pointer: coarse)') {
      return {
        matches: true, media: query, onchange: null,
        addEventListener: function(){}, removeEventListener: function(){},
        addListener: function(){}, removeListener: function(){},
        dispatchEvent: function(){ return false; }
      };
    }
    return orig(query);
  };
  // touch はボタン押下中のみ発生するイベント。マウスは常に画面上にあるので、
  // mousedown→touchstart, mousedown 中の mousemove→touchmove, mouseup→touchend
  // と「ボタン押下中フラグ」で対応関係を作る。
  var pressed = false;
  function fire(type, e) {
    try {
      var touch = new Touch({ identifier: 0, target: e.target, clientX: e.clientX, clientY: e.clientY });
      var touches = type === 'touchend' ? [] : [touch];
      e.target.dispatchEvent(new TouchEvent(type, {
        touches: touches, targetTouches: touches, changedTouches: [touch],
        bubbles: true, cancelable: true
      }));
    } catch (err) { /* Touch/TouchEvent constructor not supported (e.g. Desktop Safari) */ }
  }
  document.addEventListener('mousedown', function(e) { pressed = true; fire('touchstart', e); });
  document.addEventListener('mousemove', function(e) { if (pressed) fire('touchmove', e); });
  document.addEventListener('mouseup', function(e) {
    if (!pressed) return;
    pressed = false;
    fire('touchend', e);
  });
})();
</SCR>`.replaceAll('SCR', 'script')

/**
 * Cloud-save FS-hook script injected BEFORE pyxel.js when cloudSaveSessionId is set (#450).
 *
 * Validated in Phase 0 (.claude/spikes/450-fs-hook/). Mechanism:
 * - Trap `window.pyxelContext` (whole-object assign, ~pyxel.js:15) → then trap its
 *   `pyodide` setter (~pyxel.js:179) → install FS hooks the instant pyodide is
 *   assigned, race-free and BEFORE the game runs (~pyxel.js:184). Same setter-trap
 *   technique as the `_savePyxelFile` override above.
 * - FS.close (write streams only) under a tracked prefix → read final bytes →
 *   postMessage to the parent, which debounces + persists.
 * - FS.open under a tracked prefix on a read-miss → inject prefetched bytes from the
 *   in-memory map (no synchronous XHR).
 * - A `busy` reentrancy guard stops our own writeFile/readFile from recursing the
 *   hooks (else readFile loops forever and injection emits a spurious save).
 * The parent pushes files in via `{ type:'__camFs', op:'load', files }` (an entry with
 * `overwrite: true` replaces an existing file — the ranking is refreshed mid-play).
 *
 * **Tracked prefixes** (path-prefix dispatch; the parent decides who handles what):
 * - `.pyxel/` → cloud save (#450, `useCloudSave`) — private + persistent
 * - `shared/` → shared game data (#684, `useSharedData`) — public + persistent (ranking)
 * `online/` (#545, realtime) can slot in later without changing this shape.
 *
 * ⚠️ この patch は **cross-origin フレーム側（`public/pyxel-frame.html` の
 * `installCloudSave`）にも同じものがある**。どちらかだけ直すと blob 経路と
 * cross-origin 経路で挙動が食い違う（`make dev-xorigin` で通すこと）。
 */
const cloudSavePatch = `<SCR>
(function () {
  "use strict";
  // ".pyxel/" は Pyxel の user_data_dir() が返す保存ルート（BASE_DIR=".pyxel"）。
  // ネイティブ互換のため C&M はこの標準作法に一本化する（設計: doc/architecture/cloud_save.md）。
  var SAVE_PREFIX = ".pyxel/";
  var SHARED_PREFIX = "shared/";  // #684 共有・永続データ（ランキング）
  var WORKDIR = "/pyxel_working_directory";
  window.__camSavedata = window.__camSavedata || {};
  var fsRef = null;

  // ⚠️ **基準は「そのときの cwd」**。.pyxapp を再生すると Pyxel は
  // /tmp/.pyxel/play/<id>/<hash>/ に展開して **そこへ chdir する**ので、
  // WORKDIR 固定で解釈すると user_data_dir() の書き込みが「絶対パスのまま」
  // 親へ流れ、保存 API に filename として通らない（#684 で実測して発覚）。
  function baseDir() {
    var base = WORKDIR;
    try { if (fsRef && fsRef.cwd) base = fsRef.cwd() || WORKDIR; } catch (e) {}
    return base.charAt(base.length - 1) === "/" ? base : base + "/";
  }
  function logicalPath(p) {
    if (p.charAt(0) !== "/") return p;  // すでに相対 = 論理パス
    var base = baseDir();
    // base が "/" のときに剥がすと、あらゆる絶対パスが「相対」に化けて
    // WORKDIR の分岐が死ぬ（追跡パスを取りこぼす）。ルートは基準に使わない。
    if (base !== "/" && p.indexOf(base) === 0) return p.slice(base.length);
    if (p.indexOf(WORKDIR + "/") === 0) return p.slice(WORKDIR.length + 1);
    return p;
  }
  // 追跡する特殊パス。プレフィックスで振り分け、親側ブリッジがどちらを扱うか決める
  // （savedata = .pyxel/ / 共有データ = shared/。設計: doc/architecture/cloud_save.md）。
  // **cwd 相対の先頭一致だけを見る**。「パスのどこかに /.pyxel/ を含む」で見ていた頃は、
  // 展開先そのもの（/tmp/.pyxel/play/...）に置かれる .py や .pyxres まで拾っていた。
  function isTracked(p) {
    var lp = logicalPath(p);
    return lp.indexOf(SAVE_PREFIX) === 0 || lp.indexOf(SHARED_PREFIX) === 0;
  }
  function absOf(p) { return p.charAt(0) === "/" ? p : baseDir() + p; }

  var busy = false;
  function guard(fn) { var prev = busy; busy = true; try { return fn(); } finally { busy = prev; } }

  function ensureDirs(fs, absFile) {
    var parts = absFile.split("/"); parts.pop();
    var cur = "";
    for (var i = 0; i < parts.length; i++) {
      if (!parts[i]) continue;
      cur += "/" + parts[i];
      try { if (!fs.analyzePath(cur).exists) fs.mkdir(cur, 0o777); } catch (e) {}
    }
  }
  function injectOne(fs, lp, overwrite) {
    var abs = absOf(lp);
    try {
      if (window.__camSavedata[lp] && (overwrite || !fs.analyzePath(abs).exists)) {
        ensureDirs(fs, abs);
        guard(function () { fs.writeFile(abs, window.__camSavedata[lp]); });
      }
    } catch (e) { console.error("[camSave] inject", e); }
  }

  function installHooks(pyodide) {
    if (window.__camHooksInstalled) return;
    var fs = pyodide.FS; fsRef = fs;
    for (var lp in window.__camSavedata) injectOne(fs, lp);

    var open = fs.open.bind(fs);
    fs.open = function (path, flags, mode) {
      try {
        if (!busy && isTracked(path)) {
          var abs = absOf(path);
          // 書き込みで開かれることもあるので、**開く前に必ず親ディレクトリを作る**。
          // shared/ はゲームが書く先で、注入が無くても存在していないといけない
          // （無いと open("shared/score.json","w") が落ちる）。cwd は再生の途中で
          // 変わるので、install 時に 1 度作るだけでは間に合わない。
          ensureDirs(fs, abs);
          if (!fs.analyzePath(abs).exists) injectOne(fs, logicalPath(abs));
        }
      } catch (e) { console.error("[camSave] open", e); }
      return open(path, flags, mode);
    };

    var close = fs.close.bind(fs);
    fs.close = function (stream) {
      var path = stream && stream.path;
      var writable = !!stream && (stream.flags & 3) !== 0;
      var r = close(stream);
      try {
        if (!busy && writable && path && isTracked(path)) {
          var data = guard(function () { return fs.readFile(path); });
          var lp = logicalPath(path);
          window.__camSavedata[lp] = data;
          window.parent.postMessage({ type: "__camFs", op: "save", path: lp, bytes: data }, "*");
        }
      } catch (e) { console.error("[camSave] close", e); }
      return r;
    };
    window.__camHooksInstalled = true;
  }

  function installPyodideTrap(ctx) {
    var _pyodide = ctx.pyodide || null;
    try {
      Object.defineProperty(ctx, "pyodide", {
        configurable: true,
        get: function () { return _pyodide; },
        set: function (v) { _pyodide = v; if (v) { try { installHooks(v); } catch (e) { console.error("[camSave] install", e); } } }
      });
    } catch (e) { console.error("[camSave] pyodide trap", e); }
    if (_pyodide) installHooks(_pyodide);
  }

  var _ctx = window.pyxelContext;
  try {
    Object.defineProperty(window, "pyxelContext", {
      configurable: true,
      get: function () { return _ctx; },
      set: function (obj) { _ctx = obj; installPyodideTrap(obj); }
    });
  } catch (e) { console.error("[camSave] ctx trap", e); }
  if (_ctx) installPyodideTrap(_ctx);

  window.addEventListener("message", function (ev) {
    if (ev.source !== window.parent) return;
    var d = ev.data;
    if (!d || d.type !== "__camFs" || d.op !== "load" || !d.files) return;
    for (var i = 0; i < d.files.length; i++) {
      var f = d.files[i];
      if (!f || !f.path) continue;
      var lp = logicalPath(f.path);
      window.__camSavedata[lp] = f.bytes;
      if (fsRef) injectOne(fsRef, lp, !!f.overwrite);
    }
  });

  try { window.parent.postMessage({ type: "__camFs", op: "ready" }, "*"); } catch (e) {}
})();
</SCR>`.replaceAll('SCR', 'script')

/**
 * #737: "CLICK/TOUCH TO START" ゲート自動スキップ patch（autoStart 有効時のみ注入）。
 * pyxel.js の _waitForInput() が window.pyxelContext.resolveInput をセットしたら即呼ぶ。
 * prompt 画像（#pyxel-prompt）は一瞬の点滅も出ないよう CSS で隠す。無音デモ限定。
 */
const autoStartPatch = `<style>#pyxel-prompt{display:none !important}</style>
<SCR>
(function () {
  "use strict";
  var tries = 0;
  var timer = setInterval(function () {
    var ctx = window.pyxelContext;
    if (ctx && typeof ctx.resolveInput === "function") {
      ctx.resolveInput();
      clearInterval(timer);
      // #737: 実際に起動した瞬間を親へ通知。ヒーローの詠唱演出を実ロードに同期させ、
      // 遅い回線で固定タイマーが空フレームを見せるのを防ぐ（親側は最低演出時間 + 安全弁つき）。
      try { window.parent.postMessage({ type: "__pyxelAutoStarted" }, "*"); } catch (e) {}
    } else if (++tries > 600) {
      clearInterval(timer); // ~30s 安全弁
    }
  }, 50);
})();
</SCR>`.replaceAll('SCR', 'script')

// #745: 実行時エラーを親へ中継する。cross-origin フレーム（pyxel-frame.html）は
// 同じことを常設でやっているが、blob 経路にはこれが無かった。opaque origin なので
// 宛先は '*'（親側は ev.source がこの iframe かで検証する）。
const errorRelayPatch = `<SCR>
(function () {
  function relay(message, stack) {
    try {
      window.parent.postMessage({
        type: "__pyxelError", kind: "error",
        message: String(message || ""), stack: String(stack || "")
      }, "*");
    } catch (e) {}
  }
  window.addEventListener("error", function (e) {
    relay(e && e.message, e && e.error && e.error.stack);
  }, true);
  window.addEventListener("unhandledrejection", function (e) {
    var r = e && e.reason;
    relay(r && (r.message || r), r && r.stack);
  });
})();
</SCR>`.replaceAll('SCR', 'script')

const htmlTemplate = `<!DOCTYPE html>
<html>
<head>
CSP_META
</head>
<body style="${bodyStyle}">
ERROR_RELAY_PATCH
SAVE_PYXEL_FILE_OVERRIDE_PATCH
GAMEPAD_EMULATOR_PATCH
CLOUD_SAVE_PATCH
AUTO_START_PATCH
<SCR src="${PYXEL_CDN}wasm/pyxel.js"></SCR>
<pyxel-MODE
ROOT
ENTRYPOINT
CODE
GAMEPAD
PACKAGES>
</pyxel-MODE>
</body>
</html>`.replaceAll('SCR', 'script')

const htmlSrc = computed((): string => {
  const savePyxelFileOverride = props.capture ? savePyxelFileOverridePatch : ''
  const emulatorPatch = props.gamepadEmulator ? gamepadEmulatorPatch : ''
  const cloudSavePatchStr = props.cloudSaveSessionId ? cloudSavePatch : ''
  const autoStartStr = props.autoStart ? autoStartPatch : ''
  // 仮想ゲームパッドの表示判定:
  // - props.enableGamepad: 作品が仮想ゲームパッド対応を宣言している (Work.gamepadCompatible)
  // - isMobile.value: モバイル相当 (UA + max-width)
  // - props.gamepadEmulator: playground の開発テストモード (PC でも表示)
  // 上記いずれかが満たされたとき Pyxel に gamepad="enabled" を渡す。
  const gamepadAttr = (props.gamepadEmulator || (props.enableGamepad && isMobile.value))
    ? 'gamepad="enabled"'
    : ''
  const packagesAttr = props.packages ? `packages="${props.packages}"` : ''
  const cspMeta = buildCspMeta(props.root)
  const htmlText = props.code
    ? htmlTemplate
        .replace('CSP_META', cspMeta)
        .replace('ERROR_RELAY_PATCH', errorRelayPatch)
        .replace('SAVE_PYXEL_FILE_OVERRIDE_PATCH', savePyxelFileOverride)
        .replace('GAMEPAD_EMULATOR_PATCH', emulatorPatch)
        .replace('CLOUD_SAVE_PATCH', cloudSavePatchStr)
        .replace('AUTO_START_PATCH', autoStartStr)
        .replace('ROOT', '')
        .replace('ENTRYPOINT', '')
        .replace('CODE', `script="${props.code}"`)
        .replace('GAMEPAD', gamepadAttr)
        .replace('PACKAGES', packagesAttr)
        .replace('MODE', props.mode || 'run')
    : htmlTemplate
        .replace('CSP_META', cspMeta)
        .replace('ERROR_RELAY_PATCH', errorRelayPatch)
        .replace('SAVE_PYXEL_FILE_OVERRIDE_PATCH', savePyxelFileOverride)
        .replace('GAMEPAD_EMULATOR_PATCH', emulatorPatch)
        .replace('CLOUD_SAVE_PATCH', cloudSavePatchStr)
        .replace('AUTO_START_PATCH', autoStartStr)
        .replace('ROOT', `root="${props.root}"`)
        .replace('ENTRYPOINT', `name="${props.entrypoint}"`)
        .replace('CODE', '')
        .replace('GAMEPAD', gamepadAttr)
        .replace('PACKAGES', packagesAttr)
        .replace('MODE', props.mode || 'run')

  return URL.createObjectURL(new Blob([htmlText], { type: 'text/html' }))
})

// ===========================================================================
// #748/#749: cross-origin サンドボックスフレーム経路（iOS/iPadOS 26 修正 + #728 強化）
// ---------------------------------------------------------------------------
// runtimeConfig.public.pyxelSandboxOrigin が設定されていて、かつ **trusted でない**とき、
// 同一オリジン blob ではなく **別オリジンの静的 pyxel-frame.html** から配信する。別プロセスで
// Safari 26 の Wasm バグを回避し、別オリジンなので js 脱獄しても本体 origin の localStorage
// （認証トークン）に触れない。空 or trusted のときは従来の blob 経路（htmlSrc / sandboxAttr）へ
// フォールバック＝機能フラグ。
// - Phase 1（#748）: 未信頼プレイのみ cross-origin 化。
// - Phase 2（#749）: capture（クリスタル化プレビュー）も cross-origin 化。config の capture は
//   下で送出済みで、frame が installCaptureController（__pyxelCmd screencast/keypress + __pyxelInited）
//   で駆動を受ける。親側の駆動は useNativeScreencast（frameOrigin 経由）。`.pyxres` エディタは
//   PyxelResourceEditor.vue が同じ pyxel-frame.html を editResource config で使う。
// sandboxOrigin / useCrossOrigin は上部（cloud save より前）で宣言済み。

// フレームへ渡す実行設定（作品ごとの中身）。HTML には焼き込まず postMessage で送る。
const pyxelConfig = computed(() => ({
  type: '__pyxelConfig',
  mode: props.mode || 'run',
  code: props.code || undefined,
  root: props.code ? undefined : props.root,
  entrypoint: props.code ? undefined : props.entrypoint,
  gamepad: !!(props.gamepadEmulator || (props.enableGamepad && isMobile.value)),
  packages: props.packages || undefined,
  capture: !!props.capture,
  gamepadEmulator: !!props.gamepadEmulator,
  cloudSave: !!props.cloudSaveSessionId,
  autoStart: !!props.autoStart,
}))

// 設定が変わったらフレームを貼り直して再ハンドシェイク（blob 経路が htmlText 再計算で
// reload するのと同じ挙動を cross-origin でも再現）。code はサイズが大きいので URL には
// 載せず、nonce の変化だけで iframe を reload させる。
const frameNonce = ref(0)
if (import.meta.client) {
  watch(() => JSON.stringify(pyxelConfig.value), () => {
    frameNonce.value += 1
  })
}
const frameSrc = computed<string | undefined>(() => {
  if (!useCrossOrigin.value) return undefined
  const u = new URL('/pyxel-frame.html', sandboxOrigin.value)
  u.searchParams.set('parent', window.location.origin) // フレームの origin 検証用（本体オリジン）
  u.searchParams.set('pyxel', pyxelVersionStr) // #746 版指定を引き継ぐ
  if (frameNonce.value) u.searchParams.set('n', String(frameNonce.value))
  return u.toString()
})

// ハンドシェイク: フレームの __pyxelFrameReady を受けたら設定を送る。__pyxelError は
// テレメトリとして console に出す（iOS ゲートの `___trap` 監視にも使う）。
function sendConfig() {
  const win = iframeRef.value?.contentWindow
  if (!win) return
  win.postMessage(pyxelConfig.value, sandboxOrigin.value)
}
function onFrameMessage(ev: MessageEvent) {
  // 送信元がこの iframe であることが本質的な検証。cross-origin ではさらに
  // origin を厳格化する（blob 経路は opaque origin = "null" なので origin では見ない）。
  if (ev.source !== iframeRef.value?.contentWindow) return
  if (useCrossOrigin.value && ev.origin !== sandboxOrigin.value) return
  const d = ev.data as { type?: string, kind?: string, message?: string, source?: string, stack?: string } | null
  if (!d) return
  if (d.type === '__pyxelFrameReady') {
    if (useCrossOrigin.value) sendConfig()
  }
  else if (d.type === '__pyxelError') {
    console.error('[PyxelPlayer:frame]', d.kind, d.message, d.source ?? '', d.stack ?? '')
    // #745: Python が止まった理由を親へ。センパイの自発ヒントが使う。
    const info = parsePythonError(`${d.message ?? ''}\n${d.stack ?? ''}`)
    if (info) emit('runtimeError', info)
  }
}
onMounted(() => window.addEventListener('message', onFrameMessage))
onUnmounted(() => window.removeEventListener('message', onFrameMessage))

// blob（同一オリジン）/ cross-origin（別オリジン frame）を単一の iframe 属性に統一。
const iframeSrc = computed<string | undefined>(() =>
  useCrossOrigin.value ? frameSrc.value : htmlSrc.value,
)
const iframeSandbox = computed(() =>
  // 本物の別オリジンなので allow-same-origin を戻せる（フレームは自身のサンドボックス
  // origin に same-origin・本体には cross-origin）。#728 opaque origin より堅い。
  useCrossOrigin.value ? 'allow-scripts allow-same-origin allow-downloads' : sandboxAttr.value,
)
</script>

<template>
  <ClientOnly>
    <div
      class="pixel-player"
      :data-fill-parent="props.fillParent ? '' : null"
      :data-emulator="props.gamepadEmulator ? '' : null"
    >
      <iframe
        ref="iframeRef"
        :src="iframeSrc"
        :sandbox="iframeSandbox"
        :allow="allowAttr"
        class="pixel-player-inner"
      />
    </div>
  </ClientOnly>
</template>

<style scoped>
.pixel-player {
  height: 100%;
  margin: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.pixel-player .pixel-player-inner {
  width: 88vw;
  height: 66vw;
  max-width: 117.33vh;
  max-height: 88vh;
}

.pixel-player[data-fill-parent] {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.pixel-player[data-fill-parent] .pixel-player-inner {
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  border: 0;
  display: block;
}

/* gamepadEmulator モード: iframe を 4:5 の縦長に。
   ノーマルモードの高さ(66vw)を保ちつつ、幅を 4:5 比率で縮める */
.pixel-player[data-emulator] .pixel-player-inner {
  width: 52.8vw;
  height: 66vw;
  max-width: 70.4vh;
  max-height: 88vh;
}
</style>
